import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useLocation, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';

import Topbar from '../../../PulseSurvey/Topbar';
import Loader from '../../../../component/common/Loader';
import BasicSchemeForm from '../../../../component/schemes/BasicSchemeForm';
import PlanDetailsManagement from '../../../../component/schemes/PlanDetailsManagement';
import ConfirmPopup from '../../../../component/common/modals/ConfirmPopup';
import { CompanyApi } from '../../../../api/companyApi';

const initialFormData = {
  companyId: '',
  startDate: '',
  expiryDate: '',
  billingAmount: 0,
  providerName: '',
  providerContact: '',
  providerEmail: '',
  providerWebsite: '',
  planDetails: []
};

const AddEditScheme = () => {
  const navigate = useNavigate();
  const { search } = useLocation();
  const queryParams = new URLSearchParams(search);
  const schemeId = queryParams.get('schemeId');
  const companyId = localStorage.getItem('userData')
    ? JSON.parse(localStorage.getItem('userData')).companyId
    : '';
  const userData = localStorage.getItem('userData')
    ? JSON.parse(localStorage.getItem('userData'))
    : null;
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    ...initialFormData,
    companyId: companyId || ''
  });
  const [errors, setErrors] = useState({});
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  const [hasExistingScheme, setHasExistingScheme] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Load scheme data on page load
  useEffect(() => {
    if (companyId) {
      loadSchemeData(schemeId);
    }
  }, [companyId]);

  const loadSchemeData = async (id) => {
    setLoading(true);
    try {
      const response = await CompanyApi.getSalarySacSchemeForCompany(companyId);

      if (response?.data?.meta?.code === 1 && response?.data?.data) {
        const schemeData = response?.data?.data;

        // Map API response to form structure
        const mappedData = {
          schemeId: schemeData._id || schemeData.schemeId || id,
          companyId: schemeData.companyId || companyId,
          startDate: schemeData.startDate ? schemeData.startDate.split('T')[0] : '',
          expiryDate: schemeData.expiryDate ? schemeData.expiryDate.split('T')[0] : '',
          billingAmount: schemeData.billingAmount || 0,
          providerName: schemeData.providerName || '',
          providerContact: schemeData.providerContact || '',
          providerEmail: schemeData.providerEmail || '',
          providerWebsite: schemeData.providerWebsite || '',
          planDetails: schemeData.planDetails || []
        };

        setHasExistingScheme(true);
        setIsEditMode(false); // Start in view mode
        setFormData(mappedData);
      } else {
        setHasExistingScheme(false);
        setIsEditMode(true); // If no scheme exists, allow creation
      }
    } catch (error) {
      console.error('Error loading scheme:', error);
      setHasExistingScheme(false);
      setIsEditMode(true);
    } finally {
      setLoading(false);
    }
  };

  const handleFormDataChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value
    }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  const handlePlanDetailsChange = (planDetails) => {
    setFormData((prev) => ({
      ...prev,
      planDetails
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    // Required field validations

    if (!formData.startDate) {
      newErrors.startDate = 'Start date is required';
    }

    if (!formData.expiryDate) {
      newErrors.expiryDate = 'Expiry date is required';
    }

    if (
      formData.startDate &&
      formData.expiryDate &&
      new Date(formData.startDate) >= new Date(formData.expiryDate)
    ) {
      newErrors.expiryDate = 'Expiry date must be after start date';
    }

    if (!formData.billingAmount || formData.billingAmount <= 0) {
      newErrors.billingAmount = 'Billing amount must be greater than 0';
    }

    if (!formData.providerName?.trim()) {
      newErrors.providerName = 'Provider name is required';
    }

    if (
      formData.providerEmail?.trim() &&
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.providerEmail)
    ) {
      newErrors.providerEmail = 'Please enter a valid email address';
    }

    // Plan details validation
    if (formData.planDetails.length === 0) {
      newErrors.planDetails = 'At least one plan detail is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      toast.error('Please fix the errors before submitting');
      return;
    }

    setLoading(true);
    try {
      // Prepare payload
      const payload = {
        companyId: formData.companyId,
        startDate: formData.startDate,
        expiryDate: formData.expiryDate,
        billingAmount: formData.billingAmount,
        providerName: formData.providerName,
        providerContact: formData.providerContact || undefined,
        providerEmail: formData.providerEmail,
        providerWebsite: formData.providerWebsite || undefined,
        planDetails: formData.planDetails.filter(
          (detail) => detail.title?.trim() && detail.description?.trim()
        )
      };

      // TODO: Replace with actual API call
      const response = await CompanyApi.createOrUpdateSalarySacrificeScheme(
        payload,
        hasExistingScheme ? formData.schemeId : null
      );
      setLoading(false);
      if (response?.data?.meta?.code === 1) {
        toast.success(
          `Salary sacrifice scheme ${hasExistingScheme ? 'updated' : 'created'} successfully`
        );
        // Navigate back to salary sacrifice page
        navigate(`/${userData.slug}/salary-sacrifice`);
      }
    } catch (error) {
      console.error('Error saving scheme:', error);

      setLoading(false);
    }
  };

  const handleEditToggle = () => {
    setIsEditMode(!isEditMode);
    if (isEditMode && hasExistingScheme) {
      // If canceling edit, reload original data
      loadSchemeData(formData.schemeId);
    }
  };

  const handlePreviewToggle = () => {
    setIsPreviewMode(!isPreviewMode);
  };

  const handleDelete = () => {
    setShowDeleteConfirm(true);
  };

  const handleDeleteCancel = () => {
    setShowDeleteConfirm(false);
  };

  const confirmDelete = async () => {
    if (!formData.schemeId) return;

    setIsDeleting(true);
    try {
      const response = await CompanyApi.deleteSalarySacrificeScheme(formData.schemeId);

      if (response?.data?.meta?.code === 1) {
        setShowDeleteConfirm(false);
        // Navigate back to salary sacrifice page
        navigate(`/${userData.slug}/salary-sacrifice`);
      }
    } catch (error) {
      console.error('Error deleting scheme:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="relative">
      {loading && <Loader />}
      <Helmet>
        <meta charSet="utf-8" />
        <title>
          {hasExistingScheme ? (isEditMode ? 'Edit Scheme' : 'View Scheme') : 'Create Scheme'} -
          Shoorah
        </title>
      </Helmet>

      <Topbar
        title={hasExistingScheme ? (isEditMode ? 'Edit Scheme' : 'View Scheme') : 'Create Scheme'}
        hasDesc={true}
        description="Create and manage comprehensive business schemes with detailed plan information"
        infoBtn={false}
      />

      <div className="flex flex-col w-full max-w-7xl mt-8 space-y-6">
        {/* Header with Edit/Delete Buttons - Only show when scheme exists */}
        {hasExistingScheme && (
          <div className="bg-white dark:bg-shoorah-darkBgTabColor border border-gray-300 dark:border-gray-600 rounded-2xl p-6">
            <div className="flex justify-between items-center">
              <div>
                <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
                  {isEditMode ? 'Edit Salary Sacrifice Scheme' : 'View Salary Sacrifice Scheme'}
                </h1>
                <p className="text-gray-600 dark:text-gray-400 mt-2">
                  {isEditMode
                    ? 'Update your existing salary sacrifice scheme details'
                    : 'Review your current salary sacrifice scheme configuration'}
                </p>
              </div>

              <div className="flex items-center gap-4">
                {/* Edit/Cancel Button */}
                <button
                  onClick={handleEditToggle}
                  className={isEditMode ? 'btn-secondary' : 'btn-primary'}
                >
                  {isEditMode ? 'Cancel Edit' : 'Edit Scheme'}
                </button>

                {/* Delete Button - Only show in view mode */}
                {!isEditMode && (
                  <button
                    onClick={handleDelete}
                    className="px-6 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg text-sm font-medium transition-colors"
                  >
                    Delete Scheme
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {!isPreviewMode ? (
          <>
            {/* Basic Scheme Form */}
            <BasicSchemeForm
              formData={formData}
              errors={errors}
              onChange={handleFormDataChange}
              disabled={hasExistingScheme && !isEditMode}
            />

            {/* Plan Details Management */}
            <PlanDetailsManagement
              title="Plan Details"
              planDetails={formData.planDetails}
              onChange={handlePlanDetailsChange}
              disabled={hasExistingScheme && !isEditMode}
              placeholder="No plan details added yet. Add detailed information about your scheme plans."
            />

            {/* Action Buttons - only show in edit mode */}
            {isEditMode && (
              <div className="bg-white dark:bg-shoorah-darkBgTabColor border border-gray-300 dark:border-gray-600 rounded-2xl p-6">
                <div className="flex justify-between items-center">
                  <div className="text-sm text-gray-600 dark:text-gray-400">
                    <p>
                      <span className="font-medium">Total Plan Details:</span>{' '}
                      {formData.planDetails.length}
                    </p>
                    {errors.planDetails && (
                      <p className="text-red-500 text-sm mt-1">{errors.planDetails}</p>
                    )}
                  </div>

                  <div className="flex gap-4">
                    <button
                      onClick={hasExistingScheme ? handleEditToggle : () => navigate(-1)}
                      className="bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 px-6 py-3 rounded-lg font-medium transition-colors"
                    >
                      Cancel
                    </button>

                    <button
                      onClick={handleSubmit}
                      disabled={loading}
                      className="btn-primary"
                    >
                      {loading
                        ? hasExistingScheme
                          ? 'Updating...'
                          : 'Creating...'
                        : hasExistingScheme
                        ? 'Update Scheme'
                        : 'Create Scheme'}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        ) : (
          <>
            {/* Preview Mode */}
            <div className="bg-white dark:bg-shoorah-darkBgTabColor border border-gray-300 dark:border-gray-600 rounded-2xl p-6">
              <div className="text-center mb-8">
                <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
                  {formData.providerName} Scheme
                </h2>
                <p className="text-lg text-gray-600 dark:text-gray-400 mb-6">
                  Company ID: {formData.companyId}
                </p>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-2xl mx-auto">
                  <div className="text-center">
                    <p className="text-2xl font-bold text-shoorah-primary">
                      £{formData.billingAmount}
                    </p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Billing Amount</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-shoorah-primary">
                      {formData.planDetails.length}
                    </p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Plan Details</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-shoorah-primary">
                      {new Date(formData.startDate).toLocaleDateString()}
                    </p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Start Date</p>
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold text-shoorah-primary">
                      {Math.ceil(
                        (new Date(formData.expiryDate) - new Date(formData.startDate)) /
                          (1000 * 60 * 60 * 24)
                      )}
                    </p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">Days Duration</p>
                  </div>
                </div>
              </div>

              {/* Provider Information */}
              <div className="border-t pt-6 mb-8">
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                  Provider Information
                </h3>
                <div className="grid md:grid-cols-2 gap-4 text-sm">
                  <p>
                    <span className="font-medium">Provider:</span> {formData.providerName}
                  </p>
                  <p>
                    <span className="font-medium">Email:</span> {formData.providerEmail}
                  </p>
                  {formData.providerContact && (
                    <p>
                      <span className="font-medium">Contact:</span> {formData.providerContact}
                    </p>
                  )}
                  {formData.providerWebsite && (
                    <p>
                      <span className="font-medium">Website:</span>
                      <a
                        href={formData.providerWebsite}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-shoorah-primary hover:underline ml-1"
                      >
                        {formData.providerWebsite}
                      </a>
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Preview Plan Details */}
            {formData.planDetails.length > 0 && (
              <PlanDetailsManagement
                title="Plan Details"
                planDetails={formData.planDetails}
                onChange={() => {}}
                disabled={true}
              />
            )}
          </>
        )}
      </div>

      {/* Delete Confirmation Popup */}
      <ConfirmPopup
        open={showDeleteConfirm}
        setOpen={setShowDeleteConfirm}
        setAccepted={confirmDelete}
        handleNo={handleDeleteCancel}
        message="Are you sure you want to delete this salary sacrifice scheme? This action cannot be undone and will remove all associated data."
        isLoading={isDeleting}
      />
    </div>
  );
};

export default AddEditScheme;
